import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, Tooltip, Typography, keyframes,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, useTheme,
} from '@mui/material';
import type { DamFolderQuotaEntry, MetricDef } from '../Settings/reportDsl';
import { getInfralytiqsReportTableHeaderCellSx } from './infralytiqsReportTableTheme';
import {
  decimalMbPerTb,
  formatMaxStorageTb,
  getStorageQuotaPercent,
  StorageQuotaCellNoData,
} from './DamStorageQuotaBar';
import { formatDecimalMegabytes } from './formatStorageBytes';
import {
  buildReportTableCsv,
  downloadCsvFile,
  sanitizeCsvFilename,
} from './exportReportTableCsv';

const STORAGE_BAR_COLORS = [
  '#10b981', '#3b82f6', '#f97316', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444',
];

const quotaGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(239, 68, 68, 0.55), 0 0 18px rgba(239, 68, 68, 0.35); }
  50% { box-shadow: 0 0 14px rgba(239, 68, 68, 0.85), 0 0 28px rgba(239, 68, 68, 0.55); }
`;

export interface DamFolderStorageSlice {
  path: string;
  usedMb: number;
  maxStorageTb?: number;
  /** Max capacity used for bar math (configured max or current used when unset). */
  effectiveMaxMb: number;
  maxConfigured: boolean;
  assetCount?: number;
  /** False when no snapshot rows exist for this folder in the selected time range. */
  hasSnapshotData: boolean;
}

export interface DamMultiFolderStorageQuotaBarProps {
  folderQuotas: DamFolderQuotaEntry[];
  rows: Record<string, unknown>[];
  metrics: Array<Pick<MetricDef, 'field' | 'label' | 'fn'>>;
  /** Used as the CSV download filename stem. */
  exportTitle?: string;
  /** Registers CSV download handler for the panel title bar. */
  onRegisterDownload?: (handler: (() => void) | null) => void;
  /**
   * Show the aggregate percentage overlay on the combined bar. Defaults to
   * false — the per-segment hover tooltip already conveys the breakdown, so the
   * overlay is opt-in via the report panel config.
   */
  showBarPercentage?: boolean;
}

export const FOLDER_STORAGE_CSV_COLUMNS = [
  { key: 'path', label: 'DAM folder path' },
  { key: 'selected', label: 'Selected in bar' },
  { key: 'currentUsage', label: 'Current usage' },
  { key: 'usagePercent', label: 'Usage %' },
  { key: 'totalCapacity', label: 'Total capacity' },
  { key: 'totalAssets', label: 'Total assets' },
] as const;

const parseMetricNumber = (raw: unknown): number | undefined => {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

export const isFolderMaxConfigured = (maxStorageTb?: number): boolean =>
  typeof maxStorageTb === 'number' && Number.isFinite(maxStorageTb) && maxStorageTb > 0;

/** When no max TB is configured, treat current used storage as the folder's capacity. */
export const getEffectiveMaxMb = (usedMb: number, maxStorageTb?: number): number => {
  if (isFolderMaxConfigured(maxStorageTb)) {
    return decimalMbPerTb(maxStorageTb!);
  }
  return Math.max(0, usedMb);
};

export const formatSliceQuotaLabel = (usedMb: number, maxStorageTb?: number): string => {
  const usedLabel = formatDecimalMegabytes(usedMb);
  if (isFolderMaxConfigured(maxStorageTb)) {
    return `${usedLabel} / ${formatMaxStorageTb(maxStorageTb!)}`;
  }
  return `${usedLabel} / ${usedLabel}`;
};

export const getSliceUsagePercent = (slice: DamFolderStorageSlice): number => {
  if (slice.effectiveMaxMb <= 0) return 0;
  return getStorageQuotaPercent(slice.usedMb, slice.effectiveMaxMb / 1_000_000);
};

export const getSliceUsagePercentBadgeColor = (
  percent: number,
): 'success' | 'warning' | 'error' => {
  if (percent >= 95) return 'error';
  if (percent >= 70) return 'warning';
  return 'success';
};

export const formatSliceCurrentUsage = (slice: DamFolderStorageSlice): string => {
  if (!slice.hasSnapshotData) return 'No data';
  return formatDecimalMegabytes(slice.usedMb);
};

export const formatSliceUsagePercentLabel = (slice: DamFolderStorageSlice): string => {
  if (!slice.hasSnapshotData) return 'No data';
  return `${getSliceUsagePercent(slice).toFixed(1)}%`;
};

export const formatSliceTotalCapacity = (slice: DamFolderStorageSlice): string => {
  if (slice.maxConfigured && typeof slice.maxStorageTb === 'number') {
    return formatMaxStorageTb(slice.maxStorageTb);
  }
  if (!slice.hasSnapshotData) return 'No data';
  if (slice.effectiveMaxMb <= 0) return '—';
  return formatMaxStorageTb(slice.effectiveMaxMb / 1_000_000);
};

export const formatSliceTotalAssets = (slice: DamFolderStorageSlice): string => {
  if (!slice.hasSnapshotData) return 'No data';
  if (typeof slice.assetCount === 'number' && Number.isFinite(slice.assetCount)) {
    return Math.round(slice.assetCount).toLocaleString();
  }
  return '—';
};

export const buildFolderStorageCsvRows = (
  slices: DamFolderStorageSlice[],
  selected: Set<string>,
): Record<string, string>[] =>
  slices.map((slice) => ({
    path: slice.path,
    selected: selected.has(slice.path) ? 'Yes' : 'No',
    currentUsage: formatSliceCurrentUsage(slice),
    usagePercent: formatSliceUsagePercentLabel(slice),
    totalCapacity: formatSliceTotalCapacity(slice),
    totalAssets: formatSliceTotalAssets(slice),
  }));

/** Map ClickHouse rows (dim_0 = folder path) to configured folder quotas. */
export const buildFolderStorageSlices = (
  rows: Record<string, unknown>[],
  metrics: Array<Pick<MetricDef, 'field' | 'label' | 'fn'>>,
  folderQuotas: DamFolderQuotaEntry[],
): DamFolderStorageSlice[] => {
  const storageIdx = metrics.findIndex((m) => (m.field || '').includes('size_mb'));
  const assetIdx = metrics.findIndex((m) => (m.field || '').includes('asset_count'));
  const countIdx = metrics.findIndex((m) => m.fn === 'count');

  const usageByPath = new Map<string, { usedMb: number; assetCount?: number; snapshotCount: number }>();
  for (const row of rows) {
    const path = String(row.dim_0 ?? row['DAM folder'] ?? '').trim();
    if (!path) continue;
    const snapshotCount = countIdx >= 0
      ? parseMetricNumber(row[`metric_${countIdx}`]) ?? 0
      : 1;
    if (snapshotCount <= 0) continue;
    const usedMb = parseMetricNumber(row[`metric_${storageIdx >= 0 ? storageIdx : 0}`]) ?? 0;
    const assetCount = assetIdx >= 0
      ? parseMetricNumber(row[`metric_${assetIdx}`])
      : undefined;
    usageByPath.set(path, { usedMb, assetCount, snapshotCount });
  }

  return folderQuotas
    .filter((q) => q.path.trim())
    .map((q) => {
      const path = q.path.trim();
      const usage = usageByPath.get(path);
      const hasSnapshotData = Boolean(usage);
      const usedMb = usage?.usedMb ?? 0;
      const maxConfigured = isFolderMaxConfigured(q.maxStorageTb);
      const effectiveMaxMb = hasSnapshotData
        ? getEffectiveMaxMb(usedMb, q.maxStorageTb)
        : (maxConfigured ? decimalMbPerTb(q.maxStorageTb!) : 0);
      return {
        path,
        usedMb,
        maxStorageTb: q.maxStorageTb,
        effectiveMaxMb,
        maxConfigured,
        assetCount: usage?.assetCount,
        hasSnapshotData,
      };
    });
};

export const DamMultiFolderStorageQuotaBar: React.FC<DamMultiFolderStorageQuotaBarProps> = ({
  folderQuotas,
  rows,
  metrics,
  exportTitle,
  onRegisterDownload,
  showBarPercentage = false,
}) => {
  const slices = useMemo(
    () => buildFolderStorageSlices(rows, metrics, folderQuotas),
    [rows, metrics, folderQuotas],
  );

  const theme = useTheme();

  // Per-column text filters (matched against the displayed cell text). Filtering
  // only changes which rows are listed; slice selection (the bar) is independent.
  const [pathFilter, setPathFilter] = useState('');
  const [usageFilter, setUsageFilter] = useState('');
  const [capacityFilter, setCapacityFilter] = useState('');
  const [assetsFilter, setAssetsFilter] = useState('');

  // Column sorting. `null` preserves the configured folder order until the
  // user clicks a header. Numeric columns sort by the underlying value, with
  // "no data" / unconfigured entries pushed to the bottom (treated as -∞).
  type SortKey = 'path' | 'usage' | 'capacity' | 'assets';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'path' ? 'asc' : 'desc');
    }
  };

  const pf = pathFilter.trim().toLowerCase();
  const uf = usageFilter.trim().toLowerCase();
  const cf = capacityFilter.trim().toLowerCase();
  const af = assetsFilter.trim().toLowerCase();
  const tableSlices = slices
    .filter((s) => {
      const capLabel = s.maxConfigured ? formatSliceTotalCapacity(s).toLowerCase() : '';
      return (
        (!pf || s.path.toLowerCase().includes(pf))
        && (!uf || formatSliceCurrentUsage(s).toLowerCase().includes(uf))
        && (!cf || capLabel.includes(cf))
        && (!af || formatSliceTotalAssets(s).toLowerCase().includes(af))
      );
    })
    .slice()
    .sort((a, b) => {
      if (!sortKey) return 0;
      const sign = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'path') return sign * a.path.localeCompare(b.path);
      const num = (s: DamFolderStorageSlice): number => {
        if (sortKey === 'usage') return s.hasSnapshotData ? s.usedMb : Number.NEGATIVE_INFINITY;
        if (sortKey === 'capacity') return s.maxConfigured ? s.effectiveMaxMb : Number.NEGATIVE_INFINITY;
        return s.hasSnapshotData && typeof s.assetCount === 'number' ? s.assetCount : Number.NEGATIVE_INFINITY;
      };
      return sign * (num(a) - num(b));
    });

  const allPaths = useMemo(() => slices.map((s) => s.path), [slices]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(allPaths));

  useEffect(() => {
    setSelected(new Set(allPaths));
  }, [allPaths.join('|')]);

  const colorByPath = useMemo(() => {
    const map = new Map<string, string>();
    slices.forEach((slice, i) => {
      map.set(slice.path, STORAGE_BAR_COLORS[i % STORAGE_BAR_COLORS.length]);
    });
    return map;
  }, [slices]);

  const visibleSlices = useMemo(
    () => slices.filter((s) => selected.has(s.path)),
    [slices, selected],
  );

  const totalMaxMb = visibleSlices.reduce(
    (sum, s) => sum + (s.hasSnapshotData ? s.effectiveMaxMb : 0),
    0,
  );
  const totalUsedMb = visibleSlices.reduce(
    (sum, s) => sum + (s.hasSnapshotData ? s.usedMb : 0),
    0,
  );
  const totalPercent = getStorageQuotaPercent(totalUsedMb, totalMaxMb / 1_000_000);
  const overQuota = visibleSlices.some(
    (s) => s.maxConfigured && s.effectiveMaxMb > 0 && s.usedMb > s.effectiveMaxMb,
  ) || (totalMaxMb > 0 && totalUsedMb > totalMaxMb);

  const toggle = (path: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        if (next.size <= 1) return prev;
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleDownloadCsv = useCallback(() => {
    const csvRows = buildFolderStorageCsvRows(slices, selected);
    const csv = buildReportTableCsv([...FOLDER_STORAGE_CSV_COLUMNS], csvRows);
    downloadCsvFile(csv, sanitizeCsvFilename(exportTitle || 'dam-multi-folder-storage'));
  }, [slices, selected, exportTitle]);

  useEffect(() => {
    onRegisterDownload?.(handleDownloadCsv);
    return () => onRegisterDownload?.(null);
  }, [handleDownloadCsv, onRegisterDownload]);

  if (slices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No DAM folders configured.
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 2, px: 0.5 }}>
      {visibleSlices.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Select at least one DAM folder below.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              position: 'relative',
              height: 36,
              borderRadius: 2,
              bgcolor: 'action.hover',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              ...(overQuota && { animation: `${quotaGlow} 1.8s ease-in-out infinite` }),
            }}
          >
            {totalMaxMb > 0 && visibleSlices.map((slice) => {
              if (!slice.hasSnapshotData) return null;
              const widthPct = (slice.usedMb / totalMaxMb) * 100;
              if (widthPct <= 0) return null;
              return (
                <Tooltip
                  key={slice.path}
                  arrow
                  placement="top"
                  title={(
                    <Box sx={{ py: 0.25 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', wordBreak: 'break-all' }}>
                        {slice.path}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {`Current usage: ${formatSliceCurrentUsage(slice)}`}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {`Total capacity: ${formatSliceTotalCapacity(slice)}`}
                      </Typography>
                      {slice.maxConfigured && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {`Quota used: ${formatSliceUsagePercentLabel(slice)}`}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {`Share of bar: ${widthPct.toFixed(1)}%`}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {`Total assets: ${formatSliceTotalAssets(slice)}`}
                      </Typography>
                    </Box>
                  )}
                >
                  <Box
                    sx={{
                      width: `${widthPct}%`,
                      minWidth: widthPct > 0 ? 2 : 0,
                      height: '100%',
                      bgcolor: colorByPath.get(slice.path),
                      transition: 'width 0.45s ease',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              );
            })}
            {totalMaxMb > 0 && showBarPercentage && (
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: totalPercent > 45 ? '#fff' : 'text.primary',
                  textShadow: totalPercent > 45 ? '0 1px 2px rgba(0,0,0,0.35)' : 'none',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              >
                {totalPercent.toFixed(1)}%
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.25, textAlign: 'center', fontWeight: 500 }}
          >
            {formatDecimalMegabytes(totalUsedMb)} / {formatMaxStorageTb(totalMaxMb / 1_000_000)}
          </Typography>
        </>
      )}

      <TableContainer
        sx={{
          mt: 2,
          // Fixed height for ~10 rows + the sticky filter header; the rest scroll
          // so the panel height stays stable regardless of folder count/filters.
          height: 560,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small" stickyHeader aria-label="DAM folder storage">
          <TableHead>
            <TableRow>
              <TableCell sx={getInfralytiqsReportTableHeaderCellSx(theme, { width: '34%' })} sortDirection={sortKey === 'path' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'path'}
                  direction={sortKey === 'path' ? sortDir : 'asc'}
                  onClick={() => handleSort('path')}
                >
                  DAM folder path
                </TableSortLabel>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Filter…"
                  value={pathFilter}
                  onChange={(e) => setPathFilter(e.target.value)}
                  sx={{ mt: 0.5, width: '100%' }}
                />
              </TableCell>
              <TableCell sx={getInfralytiqsReportTableHeaderCellSx(theme, { width: '28%' })} sortDirection={sortKey === 'usage' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'usage'}
                  direction={sortKey === 'usage' ? sortDir : 'asc'}
                  onClick={() => handleSort('usage')}
                >
                  Current usage
                </TableSortLabel>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Filter…"
                  value={usageFilter}
                  onChange={(e) => setUsageFilter(e.target.value)}
                  sx={{ mt: 0.5, width: '100%' }}
                />
              </TableCell>
              <TableCell align="right" sx={getInfralytiqsReportTableHeaderCellSx(theme, { width: '18%' })} sortDirection={sortKey === 'capacity' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'capacity'}
                  direction={sortKey === 'capacity' ? sortDir : 'asc'}
                  onClick={() => handleSort('capacity')}
                >
                  Total capacity
                </TableSortLabel>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Filter…"
                  value={capacityFilter}
                  onChange={(e) => setCapacityFilter(e.target.value)}
                  sx={{ mt: 0.5, width: '100%' }}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </TableCell>
              <TableCell align="right" sx={getInfralytiqsReportTableHeaderCellSx(theme, { width: '20%' })} sortDirection={sortKey === 'assets' ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === 'assets'}
                  direction={sortKey === 'assets' ? sortDir : 'asc'}
                  onClick={() => handleSort('assets')}
                >
                  Total assets
                </TableSortLabel>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Filter…"
                  value={assetsFilter}
                  onChange={(e) => setAssetsFilter(e.target.value)}
                  sx={{ mt: 0.5, width: '100%' }}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableSlices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                    No DAM folders match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : tableSlices.map((slice) => {
              const isOn = selected.has(slice.path);
              const color = colorByPath.get(slice.path) ?? STORAGE_BAR_COLORS[0];
              const usagePercent = getSliceUsagePercent(slice);
              const usageBadgeColor = getSliceUsagePercentBadgeColor(usagePercent);
              return (
                <TableRow
                  key={slice.path}
                  hover
                  sx={{
                    opacity: isOn ? 1 : 0.65,
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Chip
                      label={slice.path}
                      size="medium"
                      clickable
                      onClick={() => toggle(slice.path)}
                      variant={isOn ? 'filled' : 'outlined'}
                      sx={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderColor: color,
                        bgcolor: isOn ? `${color}22` : 'transparent',
                        fontWeight: isOn ? 600 : 400,
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-all',
                          py: 0.75,
                        },
                        '&:hover': { bgcolor: `${color}33` },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    {!slice.hasSnapshotData ? (
                      <StorageQuotaCellNoData />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" component="span">
                          {formatSliceCurrentUsage(slice)}
                        </Typography>
                        {/* The usage % is only meaningful against a configured
                            capacity — hide the badge for folders with no max. */}
                        {slice.maxConfigured && (
                          <Chip
                            label={formatSliceUsagePercentLabel(slice)}
                            size="small"
                            color={usageBadgeColor}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }} align="right">
                    {/* No configured capacity → show the unplugged icon rather
                        than inferring a capacity from current usage. */}
                    {!slice.maxConfigured ? (
                      <StorageQuotaCellNoData />
                    ) : (
                      <Typography variant="body2">
                        {formatSliceTotalCapacity(slice)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }} align="right">
                    {!slice.hasSnapshotData ? (
                      <StorageQuotaCellNoData />
                    ) : (
                      <Typography variant="body2">
                        {formatSliceTotalAssets(slice)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
